<template>
	<VerticalLayout>
		<Row :gutter="[16, 16]">
			<Col :span="18" :offset="0">
				<VerticalLayout>
					<StatsChartTitleWithDescription
						titleKey="stats.brdcm.coverHeatmap.chartTitle"
						descriptionKey="stats.brdcm.coverHeatmap.chartTitleTooltip"
						titleClass="pd-lg-x pd-md-top"
					/>
					<HeatMapChart
						v-if="datesRangeStr != null"
						:isLoading="isLoading"
						:series="coverHeatmapSeries"
						:categories="coverHeatmapCategories"
						colorSettingsMode="exact"
						:colorSettings="coverColorSettings"
						:noDataLabel="$t('stats.brdcm.legend.noData')"
						showLabelNamesInTooltip
						tooltip-wrap
						:tooltip-max-width="BRDCM_HEATMAP_TOOLTIP_MAX_WIDTH"
						:xAxisLabel="$t('stats.brdcm.xAxisLabel')"
					/>
					<StatsChartTitleWithDescription
						titleKey="stats.brdcm.msmHeatmap.chartTitle"
						descriptionKey="stats.brdcm.msmHeatmap.chartTitleTooltip"
						titleClass="pd-lg-x pd-md-top"
					/>
					<HeatMapChart
						v-if="datesRangeStr != null"
						:isLoading="isLoading"
						:series="msmHeatmapSeries"
						:categories="msmHeatmapCategories"
						colorSettingsMode="exact"
						:colorSettings="msmColorSettings"
						:noDataLabel="$t('stats.brdcm.legend.noData')"
						showLabelNamesInTooltip
						tooltip-wrap
						:tooltip-max-width="BRDCM_HEATMAP_TOOLTIP_MAX_WIDTH"
						:xAxisLabel="$t('stats.brdcm.xAxisLabel')"
					/>
				</VerticalLayout>
			</Col>
			<Col :span="6" :offset="0">
				<Card :bordered="false">
					<VerticalLayout>
						<div class="pd-vertical">
							<span>{{ $t('controls.gnssSelect.label') }}</span>
							<Select
								class="width-100"
								v-model:value="gnssSelected"
								mode="multiple"
								:allowClear="true"
								:options="gnssOptions"
							/>
						</div>
						<DateRangeChooserMaxSpan
							v-if="brdcmDateRangeLimit.enabled"
							v-model="datesRange"
							:maxSpanDays="brdcmDateRangeLimit.maxDays"
							@change="fetchStatsItems"
						/>
						<DateRangeChooser
							v-else
							v-model="datesRange"
							quickPeriod="day1"
							@change="fetchStatsItems"
						/>
					</VerticalLayout>
				</Card>
			</Col>
		</Row>
	</VerticalLayout>
</template>

<script lang="ts">
import { ref, computed, onMounted } from 'vue';
import { Row, Col, Card, Select } from 'ant-design-vue';
import { useI18n } from 'vue-i18n';

import Api from '@/api/Api';

import MapHelpers from '@/components/map/utils/MapHelpers';

import { useLocalDateRange } from '@/utils/useDateRangeAdapter';
import { generateSeries, pointEntryFactoryXYDay } from '@/utils/chartsUtils';
import {
	BRDCM_GNSS_CODES,
	buildBrdcmCoverColorSettings,
	buildBrdcmMsmColorSettings,
	buildCoverChartItems,
	buildMsmChartItems,
	collectCoverSeriesKeys,
	collectMsmSeriesKeys,
	filterSatsByGnss,
} from '@/utils/brdcmHeatmapUtils';
import type {
	BrdcmHeatmapChartPointItem,
	BrdcmHeatmapTooltipLabels,
	BrdcmMergeItem,
	BrdcmStatsItem,
} from '@/utils/brdcmHeatmapUtils';
import type { HeatmapTooltipRow } from '@/utils/spoofingVisibilityHeatmapUtils';
import type { GenerateSeriesResult } from '@/utils/types/chartsUtilsTypes';

import { datetimeToString } from '@/components/common/dateUtils';

import VerticalLayout from '@/components/common/VerticalLayout.vue';
import DateRangeChooser from '@/components/common/DateRangeChooser.vue';
import DateRangeChooserMaxSpan from '@/components/common/DateRangeChooserMaxSpan.vue';
import StatsChartTitleWithDescription from '@/components/stats/StatsChartTitleWithDescription.vue';
import HeatMapChart from '@/components/charts/HeatMapChart.vue';


const BRDCM_DATE_RANGE_LIMIT = {
	enabled: true,
	maxDays: 5,
} as const;

const BRDCM_HEATMAP_TOOLTIP_MAX_WIDTH = 280;


export default {
	components: {
		Row, Col, Card, Select,
		VerticalLayout, DateRangeChooser, DateRangeChooserMaxSpan,
		StatsChartTitleWithDescription, HeatMapChart,
	},
	setup() {
		const { t } = useI18n();
		const { datesRange, datesRangeStr, datesRangeSafe } = useLocalDateRange(1, 0);
		const gnssSelected = ref<string[]>([]);
		const { apiData, isLoading, doRequest } = Api.useBrdcmStatsGet();

		const gnssOptions = BRDCM_GNSS_CODES.map(code => ({
			value: code,
			label: MapHelpers.getGnssDataDisplay(code).name,
		}));

		const fetchStatsItems = () => {
			if (datesRangeStr.value == null) {
				return;
			}
			doRequest({
				start_date: datesRangeStr.value[0],
				end_date: datesRangeStr.value[1],
			});
		};

		onMounted(() => {
			fetchStatsItems();
		});

		const filteredItems = computed(() => {
			const items = (apiData.value.items || []) as BrdcmStatsItem[];
			const allowedSats = new Set(filterSatsByGnss(
				items.map(item => item.satellite),
				gnssSelected.value,
			));
			return items.filter(item => allowedSats.has(item.satellite));
		});

		const merges = computed(() => (apiData.value.merges || []) as BrdcmMergeItem[]);

		const tooltipLabels = computed<BrdcmHeatmapTooltipLabels>(() => ({
			stations: t('stats.brdcm.tooltip.stations'),
			minimum: t('stats.brdcm.tooltip.minimum'),
			mergeId: t('stats.brdcm.tooltip.mergeId'),
			ephStations: t('stats.brdcm.tooltip.ephStations'),
			msmStations: t('stats.brdcm.tooltip.msmStations'),
		}));

		const coverChartItems = computed(() => buildCoverChartItems(
			filteredItems.value,
			merges.value,
			tooltipLabels.value,
		));

		const msmChartItems = computed(() => buildMsmChartItems(
			filteredItems.value,
			merges.value,
			tooltipLabels.value,
		));

		const coverSeriesKeys = computed(() => filterSatsByGnss(
			collectCoverSeriesKeys(filteredItems.value),
			gnssSelected.value,
		));

		const msmSeriesKeys = computed(() => filterSatsByGnss(
			collectMsmSeriesKeys(filteredItems.value),
			gnssSelected.value,
		));

		const buildHeatmapSeries = (
			chartItems: BrdcmHeatmapChartPointItem[],
			forceSeriesKeys: string[],
		): GenerateSeriesResult => {
			if (datesRangeSafe.value[0] == null || datesRangeSafe.value[1] == null) {
				return { series: [], categories: [] };
			}
			return generateSeries({
				items: chartItems as unknown as Array<Record<string, unknown>>,
				getSeriesKeyFn: item => String(item.satellite),
				getValueFn: item => (typeof item.status === 'number' ? item.status : null),
				seriesStep: apiData.value.period || 5,
				seriesStepUnit: 'minute',
				categoryFormatter: datetimeToString,
				pointEntryFactory: pointEntryFactoryXYDay,
				showNullAsZero: false,
				minDate: new Date(datesRangeSafe.value[0]),
				maxDate: new Date(datesRangeSafe.value[1]),
				forceSeriesKeys,
				roundTimeValues: true,
				pushAdditionalFields: ['tooltipRows'],
			});
		};

		const mapHeatmapSeries = (seriesCategories: GenerateSeriesResult) => seriesCategories.series.map(serie => ({
			name: serie.name,
			hidden: serie.hidden,
			data: serie.data.map(point => {
				if (Array.isArray(point)) {
					return { x: point[0], y: point[1], data: [] as HeatmapTooltipRow[] };
				}
				const tooltipRows = Array.isArray(point.tooltipRows)
					? point.tooltipRows as HeatmapTooltipRow[]
					: [];
				return {
					x: point.x,
					y: point.y,
					data: tooltipRows,
				};
			}),
		}));

		const coverHeatmapSeriesCategories = computed(() => buildHeatmapSeries(
			coverChartItems.value,
			coverSeriesKeys.value,
		));

		const msmHeatmapSeriesCategories = computed(() => buildHeatmapSeries(
			msmChartItems.value,
			msmSeriesKeys.value,
		));

		const coverHeatmapSeries = computed(() => mapHeatmapSeries(coverHeatmapSeriesCategories.value));
		const msmHeatmapSeries = computed(() => mapHeatmapSeries(msmHeatmapSeriesCategories.value));
		const coverHeatmapCategories = computed(() => coverHeatmapSeriesCategories.value.categories.map(category => String(category)));
		const msmHeatmapCategories = computed(() => msmHeatmapSeriesCategories.value.categories.map(category => String(category)));

		const coverColorSettings = computed(() => buildBrdcmCoverColorSettings({
			quorum: t('stats.brdcm.legend.quorum'),
			weak: t('stats.brdcm.legend.weak'),
			held: t('stats.brdcm.legend.held'),
		}));

		const msmColorSettings = computed(() => buildBrdcmMsmColorSettings({
			quorum: t('stats.brdcm.legend.quorum'),
			weak: t('stats.brdcm.legend.weak'),
			held: t('stats.brdcm.legend.held'),
			msmNoEph: t('stats.brdcm.legend.msmNoEph'),
		}));

		return {
			BRDCM_HEATMAP_TOOLTIP_MAX_WIDTH,
			brdcmDateRangeLimit: BRDCM_DATE_RANGE_LIMIT,
			isLoading,
			datesRange,
			datesRangeStr,
			gnssSelected,
			gnssOptions,
			fetchStatsItems,
			coverHeatmapSeries,
			coverHeatmapCategories,
			msmHeatmapSeries,
			msmHeatmapCategories,
			coverColorSettings,
			msmColorSettings,
		};
	},
};
</script>
